import { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { Highlight } from "@tiptap/extension-highlight";
import { Youtube } from "@tiptap/extension-youtube";

// TipTap ships TextStyle only with the `style` attr, so we extend it here
// to also preserve fontSize / fontFamily on the same <span> mark. Both
// attributes render as inline CSS; TextStyle's default merge logic keeps
// them alongside `color` from the Color extension.
const CustomTextStyle = TextStyle.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      fontSize: {
        default: null,
        parseHTML: (el) => el.style?.fontSize || null,
        renderHTML: (attrs) => (attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {}),
      },
      fontFamily: {
        default: null,
        parseHTML: (el) => el.style?.fontFamily || null,
        renderHTML: (attrs) => (attrs.fontFamily ? { style: `font-family: ${attrs.fontFamily}` } : {}),
      },
    };
  },
});
import EmojiPicker from "emoji-picker-react";
import {
  TextB, TextItalic, TextUnderline, TextStrikethrough,
  TextAlignLeft, TextAlignCenter, TextAlignRight, TextAlignJustify,
  ListBullets, ListNumbers, Quotes, Link as LinkI, Image as ImageI,
  ArrowUUpLeft, ArrowUUpRight, Code, CaretDown, Smiley, VideoCamera,
  Translate, DotsThreeVertical, Palette, TextAa, TextT, HighlighterCircle,
  X,
} from "@phosphor-icons/react";

/**
 * BloggerRichEditor — a Blogger-style TipTap editor. Reproduces the
 * shape of Blogger's compose toolbar (font-family / font-size / style /
 * B I U S / colours / link / image / video / emoji / translate /
 * overflow menu) while writing standards-safe HTML.
 */

const FONT_FAMILIES = [
  { label: "Default", value: "" },
  { label: "Sans-serif", value: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif" },
  { label: "Serif", value: "'IBM Plex Serif', Georgia, serif" },
  { label: "Monospace", value: "'JetBrains Mono', ui-monospace, monospace" },
  { label: "Display", value: "'Playfair Display', 'DM Serif Display', serif" },
];
const FONT_SIZES = [
  { label: "Smallest", value: "12px" },
  { label: "Small", value: "14px" },
  { label: "Normal", value: "" },
  { label: "Medium", value: "18px" },
  { label: "Large", value: "22px" },
  { label: "Largest", value: "28px" },
];
const STYLE_ITEMS = [
  { label: "Normal", cmd: (e) => e.chain().focus().setParagraph().run(), match: (e) => e.isActive("paragraph") && !e.isActive("blockquote") },
  { label: "Heading 2", cmd: (e) => e.chain().focus().toggleHeading({ level: 2 }).run(), match: (e) => e.isActive("heading", { level: 2 }) },
  { label: "Heading 3", cmd: (e) => e.chain().focus().toggleHeading({ level: 3 }).run(), match: (e) => e.isActive("heading", { level: 3 }) },
  { label: "Heading 4", cmd: (e) => e.chain().focus().toggleHeading({ level: 4 }).run(), match: (e) => e.isActive("heading", { level: 4 }) },
  { label: "Subheading", cmd: (e) => e.chain().focus().toggleHeading({ level: 5 }).run(), match: (e) => e.isActive("heading", { level: 5 }) },
  { label: "Quote", cmd: (e) => e.chain().focus().toggleBlockquote().run(), match: (e) => e.isActive("blockquote") },
  { label: "Code", cmd: (e) => e.chain().focus().toggleCodeBlock().run(), match: (e) => e.isActive("codeBlock") },
];
const TEXT_COLORS = ["#1B4332", "#B85F2A", "#0E7C57", "#245BB0", "#8A2A63", "#B37700", "#3A3830", "#D33B27"];
const HIGHLIGHT_COLORS = ["#FDE68A", "#BEE3F8", "#FED7D7", "#C6F6D5", "#FBCFE8", "#E9D8FD", "#FDF2C4", "#F0EAD6"];

// FontSize extension — TipTap doesn't ship one, but we can lean on
// TextStyle + a small custom command via attributes.
const setFontSize = (editor, size) => {
  if (!size) editor.chain().focus().unsetMark("textStyle", { extendEmptyMarkRange: true }).run();
  else editor.chain().focus().setMark("textStyle", { fontSize: size }).run();
};
const setFontFamily = (editor, family) => {
  if (!family) editor.chain().focus().unsetMark("textStyle", { extendEmptyMarkRange: true }).run();
  else editor.chain().focus().setMark("textStyle", { fontFamily: family }).run();
};

const IconBtn = ({ onClick, active, disabled, title, testid, children }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    title={title}
    data-testid={testid}
    className={
      "inline-flex items-center justify-center h-8 min-w-8 px-1.5 rounded-md text-[13px] " +
      (active ? "bg-[#E0EBF6] text-[#245BB0] " : "text-[#241209] hover:bg-black/5 ") +
      "disabled:opacity-40"
    }
  >
    {children}
  </button>
);

const Sep = () => <span className="w-px bg-black/10 mx-0.5 self-stretch" />;

const Dropdown = ({ label, active, testid, children }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((v) => !v)}
        data-testid={testid}
        className={
          "inline-flex items-center gap-1 h-8 px-2 rounded-md text-xs " +
          (active ? "bg-[#E0EBF6] text-[#245BB0]" : "text-[#241209] hover:bg-black/5")
        }
      >
        {label} <CaretDown size={10} weight="bold" />
      </button>
      {open && (
        <div
          className="absolute left-0 top-full mt-1 min-w-40 rounded-lg border border-black/10 bg-white shadow-lg py-1 z-50"
          onClick={() => setOpen(false)}
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default function BloggerRichEditor({
  value,
  onChange,
  placeholder = "Write your story…",
  testid = "rich-editor",
  onTranslate,
}) {
  const [showEmoji, setShowEmoji] = useState(false);
  const [showTextColor, setShowTextColor] = useState(false);
  const [showHighlight, setShowHighlight] = useState(false);
  const [showOverflow, setShowOverflow] = useState(false);
  const overflowRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target)) setShowOverflow(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3, 4, 5] } }),
      Underline,
      // Custom TextStyle first — extends TextStyle to preserve fontSize
      // and fontFamily attributes so the toolbar dropdowns actually apply.
      CustomTextStyle,
      Color.configure({ types: ["textStyle"] }),
      Highlight.configure({ multicolor: true }),
      Link.configure({
        openOnClick: false, autolink: true,
        HTMLAttributes: { rel: "noreferrer noopener", target: "_blank", class: "text-[color:var(--np-brand,#B85F2A)] underline" },
      }),
      Image.configure({ inline: false, allowBase64: false, HTMLAttributes: { class: "rounded-lg" } }),
      Placeholder.configure({ placeholder }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Youtube.configure({ controls: true, HTMLAttributes: { class: "rounded-lg" } }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "prose prose-sm md:prose-base max-w-none min-h-[400px] p-5 focus:outline-none " +
          "prose-headings:font-serif-display prose-a:text-[color:var(--np-brand,#B85F2A)]",
        "data-testid": `${testid}-content`,
      },
    },
    onUpdate: ({ editor: ed }) => onChange?.(ed.getHTML()),
  });

  useEffect(() => {
    if (!editor) return;
    if (value !== undefined && value !== null && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return <div className="min-h-[400px] bg-white/60 animate-pulse rounded-lg" />;

  const currentStyle = STYLE_ITEMS.find((s) => s.match(editor))?.label || "Normal";

  const insertLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL (leave blank to remove):", prev);
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };
  const insertImage = () => {
    const url = window.prompt("Image URL:");
    if (!url) return;
    editor.chain().focus().setImage({ src: url, alt: "" }).run();
  };
  const insertVideo = () => {
    const url = window.prompt("Video URL (YouTube, or paste any embed link):");
    if (!url) return;
    editor.commands.setYoutubeVideo({ src: url, width: 640, height: 360 });
  };
  const insertEmoji = (e) => {
    editor.chain().focus().insertContent(e.emoji).run();
    setShowEmoji(false);
  };
  const doTranslate = () => {
    // Delegate to page-level handler which opens the ListenTranslateBar / Google.
    onTranslate?.(editor.getHTML());
  };

  return (
    <div className="rounded-lg border border-black/15 bg-white overflow-hidden" data-testid={testid}>
      {/* Blogger-style compact toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-black/10 bg-white sticky top-0 z-10" data-testid={`${testid}-toolbar`}>
        <IconBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo" testid={`${testid}-undo`}><ArrowUUpLeft size={16} /></IconBtn>
        <IconBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo" testid={`${testid}-redo`}><ArrowUUpRight size={16} /></IconBtn>
        <Sep />

        {/* Font family */}
        <Dropdown label={<TextAa size={16} />} testid={`${testid}-font-family`}>
          {FONT_FAMILIES.map((f) => (
            <button key={f.label} type="button" onClick={() => setFontFamily(editor, f.value)} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-black/5" style={{ fontFamily: f.value || undefined }}>{f.label}</button>
          ))}
        </Dropdown>

        {/* Font size */}
        <Dropdown label={<TextT size={16} />} testid={`${testid}-font-size`}>
          {FONT_SIZES.map((s) => (
            <button key={s.label} type="button" onClick={() => setFontSize(editor, s.value)} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-black/5">{s.label}</button>
          ))}
        </Dropdown>

        {/* Style (Normal / H2..) */}
        <Dropdown label={currentStyle} testid={`${testid}-style`}>
          {STYLE_ITEMS.map((s) => (
            <button key={s.label} type="button" onClick={() => s.cmd(editor)} className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-black/5 ${s.match(editor) ? "bg-[#E0EBF6] text-[#245BB0]" : ""}`}>{s.label}</button>
          ))}
        </Dropdown>

        <Sep />
        <IconBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold" testid={`${testid}-bold`}><TextB size={15} weight="bold" /></IconBtn>
        <IconBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic" testid={`${testid}-italic`}><TextItalic size={15} weight="bold" /></IconBtn>
        <IconBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline" testid={`${testid}-underline`}><TextUnderline size={15} weight="bold" /></IconBtn>
        <IconBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Strikethrough" testid={`${testid}-strike`}><TextStrikethrough size={15} weight="bold" /></IconBtn>

        {/* Text color */}
        <div className="relative">
          <IconBtn onClick={() => { setShowTextColor((v) => !v); setShowHighlight(false); }} title="Text color" testid={`${testid}-textcolor`}><Palette size={15} weight="fill" /></IconBtn>
          {showTextColor && (
            <div className="absolute left-0 top-full mt-1 flex flex-wrap gap-1 p-2 bg-white border border-black/10 rounded-lg shadow-lg z-50 w-40">
              {TEXT_COLORS.map((c) => (
                <button key={c} type="button" onClick={() => { editor.chain().focus().setColor(c).run(); setShowTextColor(false); }} className="w-6 h-6 rounded-full border border-black/10" style={{ backgroundColor: c }} data-testid={`${testid}-textcolor-${c}`} />
              ))}
              <button type="button" onClick={() => { editor.chain().focus().unsetColor().run(); setShowTextColor(false); }} className="w-full text-[10px] mt-1 text-[#3A3830] font-mono-tech tracking-[0.2em] uppercase hover:text-black">Reset</button>
            </div>
          )}
        </div>

        {/* Highlight */}
        <div className="relative">
          <IconBtn onClick={() => { setShowHighlight((v) => !v); setShowTextColor(false); }} title="Highlight color" testid={`${testid}-highlight`}><HighlighterCircle size={15} weight="fill" /></IconBtn>
          {showHighlight && (
            <div className="absolute left-0 top-full mt-1 flex flex-wrap gap-1 p-2 bg-white border border-black/10 rounded-lg shadow-lg z-50 w-40">
              {HIGHLIGHT_COLORS.map((c) => (
                <button key={c} type="button" onClick={() => { editor.chain().focus().toggleHighlight({ color: c }).run(); setShowHighlight(false); }} className="w-6 h-6 rounded-full border border-black/10" style={{ backgroundColor: c }} data-testid={`${testid}-highlight-${c}`} />
              ))}
              <button type="button" onClick={() => { editor.chain().focus().unsetHighlight().run(); setShowHighlight(false); }} className="w-full text-[10px] mt-1 text-[#3A3830] font-mono-tech tracking-[0.2em] uppercase hover:text-black">Reset</button>
            </div>
          )}
        </div>

        <Sep />
        <IconBtn onClick={insertLink} active={editor.isActive("link")} title="Insert link" testid={`${testid}-link`}><LinkI size={15} weight="bold" /></IconBtn>
        <IconBtn onClick={insertImage} title="Insert image" testid={`${testid}-image`}><ImageI size={15} weight="fill" /></IconBtn>
        <IconBtn onClick={insertVideo} title="Insert video / YouTube" testid={`${testid}-video`}><VideoCamera size={15} weight="fill" /></IconBtn>

        <div className="relative">
          <IconBtn onClick={() => setShowEmoji((v) => !v)} title="Insert emoji" testid={`${testid}-emoji`}><Smiley size={15} weight="fill" /></IconBtn>
          {showEmoji && (
            <div className="absolute left-0 top-full mt-1 z-50">
              <EmojiPicker onEmojiClick={insertEmoji} height={340} width={280} lazyLoadEmojis />
            </div>
          )}
        </div>

        <IconBtn onClick={doTranslate} title="Translate this article" testid={`${testid}-translate`}><Translate size={15} weight="fill" /></IconBtn>

        <Sep />

        {/* Overflow */}
        <div className="relative" ref={overflowRef}>
          <IconBtn onClick={() => setShowOverflow((v) => !v)} title="More" testid={`${testid}-overflow`} active={showOverflow}><DotsThreeVertical size={16} weight="bold" /></IconBtn>
          {showOverflow && (
            <div className="absolute right-0 top-full mt-1 min-w-56 rounded-lg border border-black/10 bg-white shadow-lg p-1.5 z-50 grid grid-cols-4 gap-1">
              <IconBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bulleted list" testid={`${testid}-ul`}><ListBullets size={15} weight="bold" /></IconBtn>
              <IconBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list" testid={`${testid}-ol`}><ListNumbers size={15} weight="bold" /></IconBtn>
              <IconBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Quote" testid={`${testid}-quote`}><Quotes size={15} weight="bold" /></IconBtn>
              <IconBtn onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive("codeBlock")} title="Code block" testid={`${testid}-code`}><Code size={15} weight="bold" /></IconBtn>
              <IconBtn onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Align left" testid={`${testid}-align-left`}><TextAlignLeft size={15} weight="bold" /></IconBtn>
              <IconBtn onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Align center" testid={`${testid}-align-center`}><TextAlignCenter size={15} weight="bold" /></IconBtn>
              <IconBtn onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Align right" testid={`${testid}-align-right`}><TextAlignRight size={15} weight="bold" /></IconBtn>
              <IconBtn onClick={() => editor.chain().focus().setTextAlign("justify").run()} active={editor.isActive({ textAlign: "justify" })} title="Justify" testid={`${testid}-align-justify`}><TextAlignJustify size={15} weight="bold" /></IconBtn>
              <IconBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal rule" testid={`${testid}-hr`}>—</IconBtn>
              <IconBtn onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} title="Clear formatting" testid={`${testid}-clear`}><X size={15} weight="bold" /></IconBtn>
            </div>
          )}
        </div>
      </div>

      <EditorContent editor={editor} />
    </div>
  );
}
